import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/feature/EmptyState";
import SearchBox from "@/components/feature/SearchBox";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import { matchesKeyword, sortAnime } from "@/utils/anime";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [genre, setGenre] = useState("");

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 - 动漫免费在观看` : "搜索动漫 - 动漫免费在观看",
    keyword
      ? `在动漫免费在观看搜索${keyword}相关的动漫作品，查看匹配结果与同题材推荐，覆盖多种题材的高清动漫。`
      : "在动漫免费在观看搜索你想看的动漫，按名称、题材、地区快速查找高清动漫作品。",
    "动漫搜索,动漫在线观看,免费观看动漫,高清动漫,热门动漫",
  );

  const matched = useMemo(() => sortAnime(animeList.filter((anime) => matchesKeyword(anime, keyword)), "popular"), [keyword]);

  const genreOptions = useMemo(() => {
    const set = new Set<string>();
    matched.forEach((anime) => anime.categories.forEach((cat) => set.add(cat)));
    return Array.from(set);
  }, [matched]);

  const results = useMemo(
    () => (genre ? matched.filter((anime) => anime.categories.includes(genre)) : matched),
    [matched, genre],
  );

  const recommendations = useMemo(() => sortAnime(animeList, "popular").slice(0, 4), []);

  return (
    <AppLayout>
      <div className="px-4 md:px-6 pt-5">
        <Breadcrumb items={[{ label: "搜索" }]} />

        <header className="mt-3">
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">搜索动漫</h1>
          <p className="mt-1 text-[12.5px] text-foreground-600">
            {keyword ? (
              <>
                关键词 <strong className="text-accent-600">“{keyword}”</strong> 共匹配{" "}
                <strong className="text-foreground-900">{matched.length}</strong> 部作品
              </>
            ) : (
              "输入动漫名称、题材、地区或制作公司，快速找到想看的作品"
            )}
          </p>
          <div className="mt-4 max-w-2xl">
            <SearchBox defaultValue={keyword} />
          </div>
        </header>

        {matched.length > 0 ? (
          <>
            <div className="mt-5 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
              <span className="shrink-0 text-[11px] text-foreground-500">类别</span>
              <button
                type="button"
                onClick={() => setGenre("")}
                className={`shrink-0 px-3 h-8 rounded-full text-[12px] transition-colors cursor-pointer whitespace-nowrap ${
                  genre === "" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 border border-background-200"
                }`}
              >
                全部
              </button>
              {genreOptions.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setGenre(genre === cat ? "" : cat)}
                  className={`shrink-0 px-3 h-8 rounded-full text-[12px] transition-colors cursor-pointer whitespace-nowrap ${
                    genre === cat ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 border border-background-200"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-[12px] text-foreground-600">
                共 <strong className="text-foreground-900">{results.length}</strong> 条结果
              </p>
              <Link to="/list" className="text-[12px] text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-0.5">
                使用高级筛选
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>

            {results.length ? (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5">
                {results.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <div className="mt-4">
                <EmptyState
                  title="该类别下没有匹配结果"
                  description="试试切换回全部类别，或者换一个关键词重新搜索。"
                  onReset={() => setGenre("")}
                  resetLabel="查看全部类别"
                />
              </div>
            )}
          </>
        ) : (
          <div className="mt-5">
            <EmptyState
              icon="ri-search-eye-line"
              title={keyword ? `没有找到与“${keyword}”相关的动漫` : "还没有输入搜索内容"}
              description={
                keyword
                  ? "可能是片名太长或写法不同，试试只输入关键词的一部分，例如「星海」「魔法」「猫」。"
                  : "在上方输入框输入动漫名称或题材关键词，例如「星海航线」「奇幻」「治愈」。"
              }
            >
              <div className="mt-4">
                <p className="text-[11px] font-semibold text-foreground-500 mb-2">热门搜索</p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {["星海", "魔法", "猫咪", "治愈", "热血", "日本"].map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => setSearchParams({ q: word })}
                      className="px-2.5 py-1 rounded-full bg-background-200 text-[11px] text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            </EmptyState>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-thumb-up-line text-accent-500" />
                猜你会喜欢
              </h2>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5">
                {recommendations.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            </section>

            <section className="mt-8 flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="px-3 h-8 rounded-md border border-background-200 bg-background-100 text-[12px] text-foreground-700 flex items-center gap-1.5 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={cat.icon} />
                  {cat.name}动漫
                </Link>
              ))}
            </section>
          </div>
        )}
      </div>
      <SiteFooter />
    </AppLayout>
  );
}